import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Cell,
} from 'recharts';
import { notificar } from '../utils/notify';
import ContestacaoAnalise from '../components/ContestacaoAnalise';
import { radarPorCompetenciasIA, TIPO_ANALISE_INFO, COMPETENCIAS_POR_TIPO, LABEL_COMPETENCIA, labelTipoReuniao } from '../utils/competencias';
import '../styles/memora_op.css';

// ── Categorias de competência ──────────────────────────────────────────────
const COMPETENCIAS = [
  { key: 'comunicacao', label: 'Comunicação',         palavras: ['comunicação','comunicar','clareza','claro','clara','expressão','didática','didático','vocabulário','linguagem','vícios','vício','articulação','tom','postura','objetividade'] },
  { key: 'tecnica',     label: 'Técnica / Produto',   palavras: ['técnico','técnica','conhecimento','plataforma','produto','sistema','solução','funcionalidade','funcionalidades','demonstração','demo','bi','dados','infraestrutura','migração','integração'] },
  { key: 'fechamento',  label: 'Fechamento',           palavras: ['fechamento','fechar','proposta','contrato','decisão','avançar','avançou','comprometimento','precificação','preço','próximos passos','plano comercial','perspectiva comercial','futuro comercial','negociação'] },
  { key: 'descoberta',  label: 'Descoberta de Dor',   palavras: ['dor','dores','necessidade','necessidades','problema','desafio','descoberta','qualificação','perguntas','investigar','diagnosticar','processos','perguntas abertas','entendimento','aprofundar','explorar'] },
  { key: 'objecoes',    label: 'Gestão de Objeções',  palavras: ['objeção','objeções','resistência','contornar','responder','lidar','evasiva','evasivo','tratada','gestão da objeção'] },
  { key: 'conducao',    label: 'Condução / Controle', palavras: ['condução','conduzir','controle','ritmo','agenda','fluiu','fluência','participação','engajado','engajamento','interveio','oportuna','oportunamente','complementando','alinhando','alinhamento','direcionou'] },
];

// ── Utilitários ────────────────────────────────────────────────────────────
const normText = (s = '') =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const incluiAlguma = (texto, palavras) =>
  palavras.some(p => normText(texto).includes(normText(p)));

const scoreCompetencia = (analises, comp) => {
  let positivo = 0, negativo = 0;
  analises.forEach(a => {
    if (a.ponto_forte    && incluiAlguma(a.ponto_forte,    comp.palavras)) positivo++;
    if (a.ponto_melhoria && incluiAlguma(a.ponto_melhoria, comp.palavras)) negativo++;
  });
  const total = positivo + negativo;
  if (total === 0) return 5;
  return Math.round(((positivo / total) * 10) * 10) / 10;
};

const agruparPorPeriodo = (analises, modo) => {
  const mapa = {};
  analises.forEach(a => {
    const raw = (a.data_reuniao || a.created_at || '').replace(' ', 'T');
    const dt = new Date(raw);
    if (!raw || isNaN(dt)) return;
    let chave;
    if (modo === 'diario') {
      chave = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    } else if (modo === 'semanal') {
      const ini = new Date(dt);
      ini.setDate(dt.getDate() - dt.getDay());
      chave = ini.toISOString().slice(0, 10);
    } else {
      chave = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
    }
    if (!mapa[chave]) mapa[chave] = { periodo: chave, notas: [], count: 0 };
    mapa[chave].notas.push(parseFloat(a.nota) || 0);
    mapa[chave].count++;
  });
  return Object.values(mapa)
    .sort((a, b) => a.periodo.localeCompare(b.periodo))
    .map(g => ({
      periodo: g.periodo,
      media: Math.round((g.notas.reduce((s, n) => s + n, 0) / g.notas.length) * 10) / 10,
      count: g.count,
    }));
};

const contarPalavras = (analises, campo) => {
  const freq = {};
  const notasMap = {};
  analises.forEach(a => {
    const lista = a[campo] || [];
    lista.forEach(item => {
      const palavra = typeof item === 'string' ? item : item?.palavra;
      if (!palavra) return;
      const k = normText(palavra);
      freq[k] = (freq[k] || 0) + 1;
      if (!notasMap[k]) notasMap[k] = [];
      notasMap[k].push(parseFloat(a.nota) || 0);
    });
  });
  return Object.entries(freq)
    .map(([k, count]) => ({
      palavra: k,
      count,
      mediaNota: notasMap[k].length
        ? Math.round((notasMap[k].reduce((s, n) => s + n, 0) / notasMap[k].length) * 10) / 10
        : null,
    }))
    .sort((a, b) => b.count - a.count);
};

const formatarPeriodo = (chave, modo) => {
  if (modo === 'mensal') {
    const [ano, mes] = chave.split('-');
    const nomes = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
    return `${nomes[parseInt(mes) - 1]}/${ano.slice(2)}`;
  }
  const [a, m, d] = chave.split('-');
  return `${d}/${m}`;
};

// ── Nuvem de palavras ──────────────────────────────────────────────────────
const NuvemPalavras = ({ palavras, tipo }) => {
  if (!palavras.length) return <p className="text-muted small">Sem dados suficientes.</p>;
  const max = palavras[0].count;
  const isPos = tipo === 'positivo';
  return (
    <div className="nuvem-palavras">
      {palavras.slice(0, 30).map((p, i) => {
        const ratio = p.count / max;
        const size   = 0.68 + ratio * 0.72;
        const weight = ratio > 0.5 ? '700' : ratio > 0.25 ? '600' : '400';
        const color  = isPos
          ? `hsl(130, ${42 + ratio * 38}%, ${42 - ratio * 18}%)`
          : `hsl(0,   ${52 + ratio * 35}%, ${52 - ratio * 22}%)`;
        const palavraCurta = p.palavra.length > 45 ? p.palavra.slice(0, 45).trim() + '…' : p.palavra;
        return (
          <span
            key={i}
            className="nuvem-tag"
            data-dica={`${p.count}× · nota ${p.mediaNota ?? '—'}`}
            title={p.palavra}
            style={{ fontSize: `${size}rem`, fontWeight: weight, color }}
          >
            {palavraCurta}
          </span>
        );
      })}
    </div>
  );
};

// ── Tooltip customizado ───────────────────────────────────────────────────
const TooltipEvol = ({ active, payload, label, modo }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="card shadow-sm" style={{ padding: '8px 14px', fontSize: '0.85rem' }}>
      <strong>{formatarPeriodo(label, modo)}</strong>
      <div>Nota média: <strong>{payload[0].value}</strong></div>
      <div>Reuniões: {payload[0].payload.count}</div>
    </div>
  );
};

// ── Ponto customizado do gráfico de área ──────────────────────────────────
const CustomDotNota = ({ cx, cy }) => (
  <circle cx={cx} cy={cy} r={6} fill="#fd7e14" stroke="white" strokeWidth={2} />
);

// ── Componente principal ───────────────────────────────────────────────────
const Analise_op_memora = () => {
  const [analises, setAnalises]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [mensagem, setMensagem]       = useState('');
  const [periodoEvol, setPeriodoEvol] = useState('diario');
  const [analiseAberta, setAnaliseAberta]         = useState(null);
  const [treinamento, setTreinamento]             = useState('');
  const [carregandoTreino, setCarregandoTreino]   = useState(false);
  const [showModalTreino, setShowModalTreino]     = useState(false);
  const [tipoAnalise, setTipoAnalise]             = useState(null); // servico | comercial | cs
  const [clientePrep, setClientePrep]             = useState('');
  const [preparacao, setPreparacao]               = useState('');
  const [carregandoPrep, setCarregandoPrep]       = useState(false);
  const [showModalPrep, setShowModalPrep]         = useState(false);
  const [audioPartes, setAudioPartes]             = useState([]);   // dataURLs por parte
  const [totalPartes, setTotalPartes]             = useState(0);
  const [parteAtual, setParteAtual]               = useState(0);
  const [gerandoAudio, setGerandoAudio]           = useState(false);
  const [velocAudio, setVelocAudio]               = useState(1);
  const audioRef = useRef(null);
  const navigate = useNavigate();

  // Identidade do setor do operador (define competências, textos e cores)
  const infoTipo = TIPO_ANALISE_INFO[tipoAnalise] || null;

  useEffect(() => { notificar(mensagem); }, [mensagem]);

  useEffect(() => {
    const carregar = async () => {
      setLoading(true);
      const token = localStorage.getItem('access');
      try {
        const [res, resGrupo] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_BASE}/api/reunioes/analise-operador/`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.get(
            `${process.env.REACT_APP_API_BASE}/api/verificar-grupo/`,
            { headers: { Authorization: `Bearer ${token}` } }
          ).catch(() => null),
        ]);
        if (res.data.success) {
          setAnalises(res.data.analises);
          // link do e-mail "você recebeu uma avaliação" (?codigo=RN-...) abre a análise direto
          const codigoUrl = new URLSearchParams(window.location.search).get('codigo');
          const alvo = codigoUrl && (res.data.analises || []).find(a => a.codigo_reuniao === codigoUrl);
          if (alvo) setAnaliseAberta(alvo);
        }
        else setMensagem('Nenhuma análise encontrada para seu usuário.');
        if (resGrupo?.data?.tipo_analise) setTipoAnalise(resGrupo.data.tipo_analise);
      } catch (err) {
        setMensagem('Erro: ' + (err.response?.data?.erro || err.message));
      } finally {
        setLoading(false);
      }
    };
    carregar();
  }, []);

  // ── Dados computados ────────────────────────────────────────────────────
  const dadosEvol = useMemo(
    () => agruparPorPeriodo(analises, periodoEvol),
    [analises, periodoEvol]
  );

  // Radar da IA filtrado pelas competências do setor do operador
  const radarIA = useMemo(
    () => radarPorCompetenciasIA(analises, tipoAnalise),
    [analises, tipoAnalise]
  );

  const dadosRadar = useMemo(() => {
    // Preferência: competências avaliadas pela IA (análises novas);
    // fallback: heurística por palavras-chave (análises antigas)
    if (radarIA && radarIA.length >= 3) return radarIA;
    return COMPETENCIAS.map(c => ({
      competencia: c.label,
      score: scoreCompetencia(analises, c),
    }));
  }, [radarIA, analises]);

  // Talentos fora do setor: competências de OUTRA área em que o operador
  // se destacou (média >= 7 com pelo menos 2 avaliações da IA)
  const talentosForaDoSetor = useMemo(() => {
    if (!tipoAnalise || !COMPETENCIAS_POR_TIPO[tipoAnalise]) return [];
    const doSetor = COMPETENCIAS_POR_TIPO[tipoAnalise];
    const acc = {};
    analises.forEach(a => {
      (a.competencias || []).forEach(c => {
        if (!c || !c.competencia || c.status === 'nao_se_aplica') return;
        const nota = parseFloat(c.nota);
        if (isNaN(nota)) return;
        if (doSetor.includes(c.competencia)) return; // só as de fora do papel dele
        if (!acc[c.competencia]) acc[c.competencia] = { notas: [], evidencias: [] };
        acc[c.competencia].notas.push(nota);
        if (c.evidencia) acc[c.competencia].evidencias.push(c.evidencia);
      });
    });
    return Object.entries(acc)
      .map(([key, v]) => ({
        key,
        label: LABEL_COMPETENCIA[key] || key,
        media: Math.round((v.notas.reduce((s, n) => s + n, 0) / v.notas.length) * 10) / 10,
        ocorrencias: v.notas.length,
        evidencia: v.evidencias[0] || '',
        areas: Object.entries(COMPETENCIAS_POR_TIPO)
          .filter(([t, keys]) => t !== tipoAnalise && keys.includes(key))
          .map(([t]) => TIPO_ANALISE_INFO[t]?.label || t),
      }))
      .filter(t => t.media >= 7 && t.ocorrencias >= 2)
      .sort((a, b) => b.media - a.media);
  }, [analises, tipoAnalise]);

  // Destaque e foco de desenvolvimento (só com dados da IA)
  const compDestaque = useMemo(() =>
    radarIA && radarIA.length ? radarIA.reduce((a, b) => (b.score > a.score ? b : a)) : null,
    [radarIA]
  );
  const compFoco = useMemo(() =>
    radarIA && radarIA.length ? radarIA.reduce((a, b) => (b.score < a.score ? b : a)) : null,
    [radarIA]
  );

  const palavrasPos = useMemo(() => contarPalavras(analises, 'palavras_positivas'), [analises]);
  const palavrasNeg = useMemo(() => contarPalavras(analises, 'palavras_negativas'), [analises]);

  const correlacao = useMemo(() => {
    const todos = [
      ...palavrasPos.map(p => ({ ...p, tipo: 'positiva' })),
      ...palavrasNeg.map(p => ({ ...p, tipo: 'negativa' })),
    ].filter(p => p.mediaNota !== null && p.count >= 2)
     .sort((a, b) => b.mediaNota - a.mediaNota);
    return todos.slice(0, 20);
  }, [palavrasPos, palavrasNeg]);

  // Conta ocorrências por competência da IA que satisfazem a condição, respeitando o setor
  const contarCompetenciasIA = (lista, tipo, condicao) => {
    const chavesDoTipo = tipo && COMPETENCIAS_POR_TIPO[tipo] ? COMPETENCIAS_POR_TIPO[tipo] : null;
    const freq = {};
    lista.forEach(a => {
      (a.competencias || []).forEach(c => {
        if (!c || !c.competencia || c.status === 'nao_se_aplica') return;
        if (chavesDoTipo && !chavesDoTipo.includes(c.competencia)) return;
        const nota = parseFloat(c.nota);
        if (!condicao(isNaN(nota) ? null : nota, c.status)) return;
        const label = LABEL_COMPETENCIA[c.competencia] || c.competencia;
        freq[label] = (freq[label] || 0) + 1;
      });
    });
    return Object.entries(freq)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  };

  // Fallback por palavras-chave, sem as categorias que não fazem sentido pro setor
  const competenciasKeywordDoSetor = useMemo(() => {
    if (tipoAnalise === 'servico' || tipoAnalise === 'cs') {
      return COMPETENCIAS.filter(c => c.key !== 'fechamento');
    }
    return COMPETENCIAS;
  }, [tipoAnalise]);

  const topFortes = useMemo(() => {
    // IA: competências com nota >= 7. Se há dados da IA, não usa o fallback
    // por palavras-chave (mesmo que a lista venha vazia), para não misturar fontes.
    if (radarIA) {
      return contarCompetenciasIA(analises, tipoAnalise, (nota) => nota !== null && nota >= 7);
    }
    const freq = {};
    analises.forEach(a => {
      if (!a.ponto_forte) return;
      competenciasKeywordDoSetor.forEach(c => {
        if (incluiAlguma(a.ponto_forte, c.palavras)) {
          freq[c.label] = (freq[c.label] || 0) + 1;
        }
      });
    });
    return Object.entries(freq)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [analises, tipoAnalise, competenciasKeywordDoSetor, radarIA]);

  const topMelhorias = useMemo(() => {
    // IA: competências "não demonstradas" ou com nota abaixo de 7
    if (radarIA) {
      return contarCompetenciasIA(analises, tipoAnalise,
        (nota, status) => status === 'nao_demonstrou' || (nota !== null && nota < 7));
    }
    const freq = {};
    analises.forEach(a => {
      if (!a.ponto_melhoria) return;
      competenciasKeywordDoSetor.forEach(c => {
        if (incluiAlguma(a.ponto_melhoria, c.palavras)) {
          freq[c.label] = (freq[c.label] || 0) + 1;
        }
      });
    });
    return Object.entries(freq)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [analises, tipoAnalise, competenciasKeywordDoSetor, radarIA]);

  const clientesMedias = useMemo(() => {
    const mapa = {};
    analises.forEach(a => {
      const empresa = a.cliente_empresa?.trim() || 'Não identificado';
      const nota = parseFloat(a.nota) || 0;
      if (!mapa[empresa]) mapa[empresa] = { empresa, notas: [], reunioes: 0 };
      mapa[empresa].notas.push(nota);
      mapa[empresa].reunioes++;
    });
    return Object.values(mapa)
      .map(c => ({
        empresa: c.empresa,
        media: Math.round((c.notas.reduce((s, n) => s + n, 0) / c.notas.length) * 10) / 10,
        reunioes: c.reunioes,
      }))
      .sort((a, b) => a.media - b.media);
  }, [analises]);

  const clientesAlerta = useMemo(() => {
    const mapa = {};
    analises.forEach(a => {
      const nota = parseFloat(a.nota) || 0;
      if (nota >= 7) return;
      const empresa = a.cliente_empresa?.trim() || 'Cliente não identificado';
      if (!mapa[empresa]) mapa[empresa] = { empresa, menorNota: nota, reunioes: 0 };
      if (nota < mapa[empresa].menorNota) mapa[empresa].menorNota = nota;
      mapa[empresa].reunioes++;
    });
    return Object.values(mapa).sort((a, b) => a.menorNota - b.menorNota);
  }, [analises]);

  const mediaNota = analises.length
    ? (analises.reduce((s, a) => s + (parseFloat(a.nota) || 0), 0) / analises.length).toFixed(1)
    : '-';

  // Melhores e piores reuniões do operador (por nota)
  const melhoresReunioes = useMemo(() =>
    [...analises]
      .filter(a => a.nota != null)
      .sort((a, b) => parseFloat(b.nota) - parseFloat(a.nota))
      .slice(0, 5),
    [analises]
  );
  const pioresReunioes = useMemo(() =>
    [...analises]
      .filter(a => a.nota != null)
      .sort((a, b) => parseFloat(a.nota) - parseFloat(b.nota))
      .slice(0, 5),
    [analises]
  );

  const getBadgeNota = (nota) => {
    if (nota >= 8) return 'bg-success';
    if (nota >= 6) return 'bg-primary';
    if (nota >= 4) return 'bg-warning text-dark';
    return 'bg-danger';
  };

  const formatarData = (data) => {
    if (!data) return '-';
    const p = String(data).split('-');
    return p.length < 3 ? data : `${p[2].substring(0, 2)}/${p[1]}/${p[0]}`;
  };

  // Clientes do operador (para o select de preparação de reunião)
  const clientesDoOperador = useMemo(() => {
    const set = new Set(
      analises.map(a => a.cliente_empresa?.trim()).filter(Boolean)
    );
    return [...set].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [analises]);

  // ── Preparação para a reunião (Memor.IA) ────────────────────────────────
  const pedirPreparacao = async () => {
    if (!clientePrep) return;
    setCarregandoPrep(true);
    setPreparacao('');
    setAudioPartes([]);
    setTotalPartes(0);
    setParteAtual(0);
    setVelocAudio(1);
    setShowModalPrep(true);
    const token = localStorage.getItem('access');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/reunioes/preparar-reuniao/`,
        { cliente_empresa: clientePrep },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) setPreparacao(res.data.preparacao);
      else setPreparacao('' + (res.data.erro || 'Erro ao preparar a reunião.'));
    } catch (err) {
      setPreparacao('' + (err.response?.data?.erro || err.message));
    } finally {
      setCarregandoPrep(false);
    }
  };

  // Divide a preparação em partes para o áudio começar rápido:
  // a 1ª parte é curta (~600 chars) para ficar pronta em segundos,
  // as demais são maiores (~1800) e chegam enquanto a primeira toca
  const dividirEmPartes = (texto) => {
    const blocos = [];
    texto.split(/\n{2,}/).forEach(par => {
      if (par.length <= 600) { blocos.push(par); return; }
      let frase = '';
      par.split(/(?<=[.!?:])\s+/).forEach(fr => {
        if (frase && (frase.length + fr.length) > 600) { blocos.push(frase); frase = fr; }
        else frase = frase ? `${frase} ${fr}` : fr;
      });
      if (frase) blocos.push(frase);
    });
    const partes = [];
    let atual = '';
    blocos.forEach(b => {
      const limite = partes.length === 0 ? 600 : 1800;
      if (atual && (atual.length + b.length) > limite) { partes.push(atual); atual = b; }
      else atual = atual ? `${atual}\n\n${b}` : b;
    });
    if (atual.trim()) partes.push(atual);
    return partes;
  };

  // Narração em áudio (Gemini TTS) — partes geradas em paralelo:
  // a primeira toca assim que fica pronta, as demais emendam na sequência.
  // Serve para qualquer texto (preparação de reunião, treinamento...)
  const gerarAudio = (texto) => {
    if (!texto || gerandoAudio) return;
    const partes = dividirEmPartes(texto);
    if (!partes.length) return;
    setGerandoAudio(true);
    setTotalPartes(partes.length);
    setAudioPartes(new Array(partes.length).fill(null));
    setParteAtual(0);
    const token = localStorage.getItem('access');
    partes.forEach((textoParte, i) => {
      axios.post(
        `${process.env.REACT_APP_API_BASE}/api/reunioes/gerar-audio/`,
        { texto: textoParte },
        { headers: { Authorization: `Bearer ${token}` } }
      ).then(res => {
        if (res.data.success) {
          setAudioPartes(prev => {
            const c = [...prev];
            c[i] = `data:${res.data.mime};base64,${res.data.audio}`;
            return c;
          });
        } else if (i === 0) {
          notificar('' + (res.data.erro || 'Erro ao gerar o áudio.'));
          setGerandoAudio(false);
          setTotalPartes(0);
        }
      }).catch(err => {
        if (i === 0) {
          notificar('' + (err.response?.data?.erro || err.message));
          setGerandoAudio(false);
          setTotalPartes(0);
        }
      });
    });
  };

  // Libera o player assim que a primeira parte do áudio chega
  useEffect(() => {
    if (gerandoAudio && audioPartes[0]) setGerandoAudio(false);
  }, [audioPartes, gerandoAudio]);

  const mudarVelocidade = (v) => {
    setVelocAudio(v);
    if (audioRef.current) audioRef.current.playbackRate = v;
  };

  // Barra "Quer que eu fale pra você?" — usada nos modais de preparação e treinamento
  const renderBarraAudio = (texto) => {
    if (!texto || texto.startsWith('')) return null;
    return (
      <div className="p-3 rounded-3 mb-3 d-flex align-items-center gap-3 flex-wrap"
        style={{ background: '#f0faf5', border: '1px solid #badbcc' }}>
        {totalPartes === 0 || gerandoAudio ? (
          <>
            <button
              className="btn btn-sm fw-semibold"
              onClick={() => gerarAudio(texto)}
              disabled={gerandoAudio}
              style={{
                background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)',
                border: 'none', color: '#fff',
              }}
            >
              {gerandoAudio
                ? <><span className="spinner-border spinner-border-sm me-2"></span>Deixa comigo, já vou falar...</>
                : <><i className="fas fa-volume-up me-1"></i>Quer que eu fale pra você?</>}
            </button>
            <small className="text-muted">
              {gerandoAudio
                ? <>Só um instante, estou organizando as ideias — começo a falar em segundos. <i className="fas fa-microphone ms-1"></i></>
                : 'Sem tempo de ler? Sou eu, a Memor.IA — aperta aí que eu te conto tudo. '}
            </small>
          </>
        ) : audioPartes[parteAtual] ? (
          <>
            <audio
              ref={audioRef}
              key={parteAtual}
              src={audioPartes[parteAtual]}
              controls
              autoPlay
              style={{ height: 38, flexGrow: 1, minWidth: 240 }}
              onLoadedMetadata={() => { if (audioRef.current) audioRef.current.playbackRate = velocAudio; }}
              onEnded={() => {
                if (parteAtual + 1 < totalPartes) setParteAtual(p => p + 1);
              }}
            ></audio>
            {totalPartes > 1 && (
              <span className="badge bg-secondary">Parte {parteAtual + 1}/{totalPartes}</span>
            )}
            <div className="btn-group btn-group-sm">
              {[1, 1.25, 1.5].map(v => (
                <button
                  key={v}
                  className={`btn ${velocAudio === v ? '' : 'btn-outline-secondary'}`}
                  style={velocAudio === v ? { backgroundColor: '#198754', borderColor: '#198754', color: '#fff' } : undefined}
                  onClick={() => mudarVelocidade(v)}
                >
                  {v}x
                </button>
              ))}
            </div>
          </>
        ) : (
          <small className="text-muted">
            <span className="spinner-border spinner-border-sm me-2"></span>
            Pegando fôlego para a parte {parteAtual + 1}/{totalPartes}... <i className="fas fa-microphone ms-1"></i>
          </small>
        )}
      </div>
    );
  };

  // ── Treinamento IA ───────────────────────────────────────────────────────
  const pedirTreinamento = async () => {
    setCarregandoTreino(true);
    setTreinamento('');
    setAudioPartes([]);
    setTotalPartes(0);
    setParteAtual(0);
    setVelocAudio(1);
    setShowModalTreino(true);
    const token = localStorage.getItem('access');
    try {
      const pontosMelhoriaRaw = [
        ...new Set(
          analises
            .map(a => a.ponto_melhoria)
            .filter(Boolean)
        )
      ];

      // Áreas de melhoria: prioriza as competências mais fracas do setor
      // (avaliadas pela IA); senão usa a heurística por palavras-chave
      const areasMelhoria = radarIA && radarIA.length
        ? [...radarIA].sort((a, b) => a.score - b.score).slice(0, 3).map(d => d.competencia)
        : topMelhorias.map(a => a.name);

      // Competências do setor com nota e evidências reais das reuniões
      const evidenciasPorComp = {};
      analises.forEach(a => (a.competencias || []).forEach(c => {
        if (!c || !c.competencia) return;
        const label = LABEL_COMPETENCIA[c.competencia] || c.competencia;
        const nota = parseFloat(c.nota);
        if ((c.status === 'nao_demonstrou' || (!isNaN(nota) && nota < 7)) && c.evidencia) {
          if (!evidenciasPorComp[label]) evidenciasPorComp[label] = [];
          evidenciasPorComp[label].push(c.evidencia);
        }
      }));
      const competenciasPayload = (radarIA || []).map(d => ({
        competencia: d.competencia,
        score: d.score,
        evidencias: (evidenciasPorComp[d.competencia] || []).slice(0, 3),
      }));

      // Só as 5 palavras negativas mais recentes (analises já vêm da mais nova pra mais antiga)
      const palavrasRecentes = [];
      for (const a of analises) {
        for (const item of (a.palavras_negativas || [])) {
          const palavra = typeof item === 'string' ? item : item?.palavra;
          if (!palavra) continue;
          if (!palavrasRecentes.some(p => p.toLowerCase() === palavra.toLowerCase())) {
            palavrasRecentes.push(palavra);
          }
          if (palavrasRecentes.length >= 5) break;
        }
        if (palavrasRecentes.length >= 5) break;
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/reunioes/treinamento-operador/`,
        {
          palavras_negativas: palavrasRecentes,
          areas_melhoria: areasMelhoria,
          pontos_melhoria: pontosMelhoriaRaw,
          competencias: competenciasPayload,
          tipo_analise: tipoAnalise,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) setTreinamento(res.data.treinamento);
      else setTreinamento('' + (res.data.erro || 'Erro ao gerar treinamento.'));
    } catch (err) {
      setTreinamento('' + (err.response?.data?.erro || err.message));
    } finally {
      setCarregandoTreino(false);
    }
  };

  // ── Renderizador de markdown simples ────────────────────────────────────
  const renderTreinamento = (texto) => {
    if (!texto) return null;
    const linhas = texto.split('\n');
    const resultado = [];
    let listaAtual = [];

    const flushLista = () => {
      if (listaAtual.length > 0) {
        resultado.push(<ul key={`ul-${resultado.length}`} className="mb-3 ps-3">{listaAtual}</ul>);
        listaAtual = [];
      }
    };

    const renderInline = (text, key) => {
      const partes = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
      return (
        <span key={key}>
          {partes.map((p, i) => {
            if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
            if (p.startsWith('*') && p.endsWith('*')) return <em key={i} style={{ color: '#555' }}>{p.slice(1, -1)}</em>;
            return p;
          })}
        </span>
      );
    };

    linhas.forEach((linha, i) => {
      if (linha.startsWith('### ')) {
        flushLista();
        resultado.push(
          <h5 key={i} className="fw-bold mt-4 mb-2 pb-1" style={{ color: '#6610f2', borderBottom: '2px solid #e2d9f3', fontSize: '1rem' }}>
            {linha.slice(4)}
          </h5>
        );
      } else if (linha.startsWith('## ')) {
        flushLista();
        resultado.push(
          <h4 key={i} className="fw-bold mt-4 mb-2 pb-1" style={{ color: '#212529', borderBottom: '2px solid #e9ecef', fontSize: '1.1rem' }}>
            {linha.slice(3)}
          </h4>
        );
      } else if (linha.startsWith('# ')) {
        flushLista();
        resultado.push(
          <h3 key={i} className="fw-bold mt-3 mb-2" style={{ fontSize: '1.2rem' }}>
            {linha.slice(2)}
          </h3>
        );
      } else if (/^---+$/.test(linha.trim())) {
        flushLista();
        resultado.push(<hr key={i} className="my-3" style={{ borderColor: '#dee2e6' }} />);
      } else if (/^[\*\-]\s+/.test(linha)) {
        listaAtual.push(
          <li key={i} className="mb-2" style={{ lineHeight: '1.65' }}>
            {renderInline(linha.replace(/^[\*\-]\s+/, ''), `li-${i}`)}
          </li>
        );
      } else if (/^\d+\.\s+/.test(linha)) {
        flushLista();
        const num = linha.match(/^(\d+)\.\s+(.*)/);
        if (num) {
          resultado.push(
            <div key={i} className="d-flex gap-2 mb-2" style={{ lineHeight: '1.65' }}>
              <span className="badge rounded-pill" style={{ background: '#6610f2', minWidth: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                {num[1]}
              </span>
              <span>{renderInline(num[2], `num-${i}`)}</span>
            </div>
          );
        }
      } else if (linha.trim() === '') {
        flushLista();
        resultado.push(<div key={i} style={{ height: '6px' }} />);
      } else {
        flushLista();
        resultado.push(
          <p key={i} className="mb-1" style={{ lineHeight: '1.75' }}>
            {renderInline(linha, `p-${i}`)}
          </p>
        );
      }
    });
    flushLista();
    return resultado;
  };

  // ── Modal detalhe ────────────────────────────────────────────────────────
  const renderModal = () => {
    if (!analiseAberta) return null;
    const a = analiseAberta;
    return (
      <div
        className="modal fade show d-block"
        style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
        onClick={() => setAnaliseAberta(null)}
      >
        <div
          className="modal-dialog modal-dialog-scrollable"
          style={{ maxWidth: '900px' }}
          onClick={e => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
              <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-user-circle me-2"></i>
                {a.codigo_reuniao} — {a.cliente_empresa || a.assunto || ''}
              </h5>
              <button className="btn-close" onClick={() => setAnaliseAberta(null)}></button>
            </div>
            <div className="modal-body">
              {(a.assunto || a.analisado_por) && (
                <div className="d-flex flex-wrap gap-3 mb-2 text-muted" style={{ fontSize: '0.85rem' }}>
                  {a.assunto && <span><i className="fas fa-video me-1"></i>{a.assunto}</span>}
                  {a.analisado_por && <span><i className="fas fa-user-check me-1"></i>Analisada por <strong>{a.analisado_por}</strong></span>}
                </div>
              )}
              <div className="row g-3 mb-3">
                <div className="col-6 col-md-3 text-center">
                  <small className="text-muted d-block">Minha nota</small>
                  <span className={`badge fs-5 ${getBadgeNota(a.nota)}`}>{a.nota}/10</span>
                </div>
                <div className="col-6 col-md-3 text-center">
                  <small className="text-muted d-block">Tipo</small>
                  <span className="badge bg-secondary">{labelTipoReuniao(a.tipo_reuniao)}</span>
                </div>
                <div className="col-6 col-md-3 text-center">
                  <small className="text-muted d-block">Data</small>
                  <strong>{formatarData(a.data_reuniao)}</strong>
                </div>
                <div className="col-6 col-md-3 text-center">
                  <small className="text-muted d-block">Reunião</small>
                  <span className={`badge ${
                    a.classificacao === 'muito_boa' ? 'bg-success' :
                    a.classificacao === 'boa' ? 'bg-primary' :
                    a.classificacao === 'critica' ? 'bg-danger' : 'bg-secondary'
                  }`}>{a.classificacao?.replace('_',' ').toUpperCase() || '-'}</span>
                </div>
              </div>
              <div className="d-flex justify-content-end mb-3">
                <ContestacaoAnalise codigoReuniao={a.codigo_reuniao} notaIa={a.nota} compact />
              </div>
              {a.objetivo_melhoria && (
                <div className="alert" style={{ background: '#fff4e0', borderLeft: '4px solid #fd7e14' }}>
                  <strong><i className="fas fa-bullseye me-1" style={{ color: '#fd7e14' }}></i> Objetivo de Melhoria</strong>
                  <p className="mb-0 mt-1">{a.objetivo_melhoria}</p>
                </div>
              )}
              {a.ponto_forte && (
                <div className="alert alert-success">
                  <strong><i className="fas fa-thumbs-up me-1"></i> Ponto Forte</strong>
                  <p className="mb-0 mt-1">{a.ponto_forte}</p>
                </div>
              )}
              {a.ponto_melhoria && (
                <div className="alert alert-danger">
                  <strong><i className="fas fa-arrow-up me-1"></i> O que Melhorar</strong>
                  <p className="mb-0 mt-1">{a.ponto_melhoria}</p>
                </div>
              )}
              {a.resumo && (
                <div className="card border-info mb-3">
                  <div className="card-body">
                    <h6 className="text-info"><i className="fas fa-file-alt me-1"></i> Resumo</h6>
                    <p className="mb-0">{a.resumo}</p>
                  </div>
                </div>
              )}

              {/* Minhas competências nesta reunião */}
              {(a.competencias || []).length > 0 && (
                <div className="mb-3">
                  <h6 className="fw-bold mb-2">
                    <i className="fas fa-bullseye me-2 text-warning"></i>Minhas Competências nesta Reunião
                  </h6>
                  <div className="d-flex flex-wrap gap-2">
                    {a.competencias.map((c, i) => {
                      if (!c || !c.competencia) return null;
                      const naoAplica = c.status === 'nao_se_aplica';
                      const demonstrou = c.status === 'demonstrou';
                      return (
                        <span
                          key={i}
                          className="badge"
                          title={c.evidencia || ''}
                          style={{
                            background: naoAplica ? '#e9ecef' : demonstrou ? '#d1e7dd' : '#f8d7da',
                            color: naoAplica ? '#6c757d' : demonstrou ? '#0f5132' : '#842029',
                            fontSize: '0.8rem', fontWeight: 500,
                          }}
                        >
                          {LABEL_COMPETENCIA[c.competencia] || c.competencia}
                          {!naoAplica && c.nota !== null && c.nota !== undefined && <> · {c.nota}/10</>}
                          {naoAplica && <> · n/a</>}
                        </span>
                      );
                    })}
                  </div>
                  <small className="text-muted">Passe o mouse em cada competência para ver a evidência da transcrição</small>
                </div>
              )}

              {/* Contato do cliente */}
              {(a.cliente_nome || a.interesse_nivel || a.cliente_orcamento) && (
                <div className="d-flex flex-wrap gap-3 mb-3">
                  {a.cliente_nome && (
                    <div>
                      <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: 700 }}>CONTATO</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>
                        {a.cliente_nome}{a.cliente_cargo ? ` · ${a.cliente_cargo}` : ''}
                      </div>
                    </div>
                  )}
                  {a.interesse_nivel && (
                    <div>
                      <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: 700 }}>INTERESSE</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{a.interesse_nivel}</div>
                    </div>
                  )}
                  {a.estagio_venda && a.estagio_venda !== 'não se aplica' && (
                    <div>
                      <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: 700 }}>ESTÁGIO</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{a.estagio_venda}</div>
                    </div>
                  )}
                  {a.cliente_orcamento && (
                    <div>
                      <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: 700 }}>ORÇAMENTO</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{a.cliente_orcamento}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Dor principal */}
              {a.cliente_dor_principal && (
                <div className="alert py-2 px-3 mb-3" style={{ background: '#fff4e0', borderLeft: '4px solid #fd7e14' }}>
                  <strong style={{ fontSize: '0.78rem' }}><i className="fas fa-heart-pulse me-1" style={{ color: '#fd7e14' }}></i> DOR PRINCIPAL DO CLIENTE</strong>
                  <p className="mb-0 mt-1" style={{ fontSize: '0.88rem' }}>{a.cliente_dor_principal}</p>
                </div>
              )}

              {/* Oportunidade */}
              {a.oportunidade && (
                <div className="alert alert-success py-2 px-3 mb-3">
                  <strong style={{ fontSize: '0.78rem' }}><i className="fas fa-lightbulb me-1"></i>OPORTUNIDADE</strong>
                  <p className="mb-0 mt-1" style={{ fontSize: '0.88rem' }}>{a.oportunidade}</p>
                </div>
              )}

              {/* Pontos críticos */}
              {(a.pontos_criticos || []).length > 0 && (
                <div className="mb-3">
                  <h6 className="fw-bold mb-2">
                    <i className="fas fa-triangle-exclamation me-2 text-danger"></i>Pontos Críticos
                  </h6>
                  {a.pontos_criticos.map((p, i) => {
                    const ehRisco = (p?.tipo || '') === 'risco';
                    return (
                      <div key={i} className="d-flex gap-2 mb-2 p-2 rounded"
                        style={{ background: ehRisco ? '#fdecec' : '#eaf7ef', fontSize: '0.87rem' }}>
                        <span className="badge flex-shrink-0" style={{
                          background: ehRisco ? '#dc3545' : '#198754', height: 'fit-content',
                        }}>{ehRisco ? 'Risco' : 'Oportunidade'}</span>
                        <span>{p?.descricao || (typeof p === 'string' ? p : '')}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Objeções */}
              {(a.objecoes || []).length > 0 && (
                <div className="mb-3">
                  <h6 className="fw-bold mb-2">
                    <i className="fas fa-shield-halved me-2" style={{ color: '#0d6efd' }}></i>Objeções
                  </h6>
                  {a.objecoes.map((o, i) => (
                    <div key={i} className="d-flex gap-2 mb-1" style={{ fontSize: '0.88rem' }}>
                      <i className="fas fa-circle-dot flex-shrink-0" style={{ marginTop: 5, fontSize: '0.6rem', color: '#0d6efd' }}></i>
                      <span>{typeof o === 'string' ? o : (o?.objecao || o?.descricao || '')}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Próximos passos */}
              {(a.proximos_passos || []).length > 0 && (
                <div className="mb-3">
                  <h6 className="fw-bold mb-2">
                    <i className="fas fa-list-check me-2 text-success"></i>Próximos Passos
                  </h6>
                  {a.proximos_passos.map((p, i) => {
                    const texto = typeof p === 'string'
                      ? p
                      : [p?.responsavel, p?.acao].filter(Boolean).join(' — ') + (p?.prazo ? ` (até ${p.prazo})` : '');
                    return (
                      <div key={i} className="d-flex gap-2 mb-1" style={{ fontSize: '0.88rem' }}>
                        <i className="fas fa-arrow-right flex-shrink-0 text-success" style={{ marginTop: 4, fontSize: '0.75rem' }}></i>
                        <span>{texto}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Produtos / serviços de interesse */}
              {(a.produtos_servicos || []).length > 0 && (
                <div className="mb-3">
                  <h6 className="fw-bold mb-2">
                    <i className="fas fa-box-open me-2" style={{ color: '#6610f2' }}></i>Produtos / Serviços de Interesse
                  </h6>
                  <div className="d-flex flex-wrap gap-1">
                    {a.produtos_servicos.map((p, i) => (
                      <span key={i} className="badge bg-primary bg-opacity-10 text-primary border border-primary"
                        style={{ fontSize: '0.78rem', fontWeight: 500, whiteSpace: 'normal', textAlign: 'left' }}>
                        {typeof p === 'string' ? p : (p?.nome || '')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Minhas palavras na reunião */}
              {((a.palavras_positivas || []).length > 0 || (a.palavras_negativas || []).length > 0) && (
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <h6 className="fw-bold mb-2">
                      <i className="fas fa-smile me-2 text-success"></i>Minhas Palavras Positivas
                    </h6>
                    {(a.palavras_positivas || []).length === 0 ? (
                      <small className="text-muted">Nenhuma registrada nesta reunião.</small>
                    ) : (
                      <div className="d-flex flex-wrap gap-1">
                        {a.palavras_positivas.map((p, i) => {
                          const palavra = typeof p === 'string' ? p : p?.palavra;
                          const contexto = typeof p === 'object' ? p?.contexto : '';
                          if (!palavra) return null;
                          return (
                            <span key={i} className="badge" title={contexto || ''}
                              style={{ background: '#d1e7dd', color: '#0f5132', fontSize: '0.78rem', fontWeight: 500, whiteSpace: 'normal', textAlign: 'left' }}>
                              {palavra}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <h6 className="fw-bold mb-2">
                      <i className="fas fa-frown me-2 text-danger"></i>Minhas Palavras Negativas
                    </h6>
                    {(a.palavras_negativas || []).length === 0 ? (
                      <small className="text-muted">Nenhuma registrada nesta reunião. </small>
                    ) : (
                      <div className="d-flex flex-wrap gap-1">
                        {a.palavras_negativas.map((p, i) => {
                          const palavra = typeof p === 'string' ? p : p?.palavra;
                          const contexto = typeof p === 'object' ? p?.contexto : '';
                          if (!palavra) return null;
                          return (
                            <span key={i} className="badge" title={contexto || ''}
                              style={{ background: '#f8d7da', color: '#842029', fontSize: '0.78rem', fontWeight: 500, whiteSpace: 'normal', textAlign: 'left' }}>
                              {palavra}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Observações da Memor.IA */}
              {a.observacoes && (
                <div className="p-3 rounded-3" style={{ background: '#f8f9fa', fontSize: '0.87rem' }}>
                  <strong style={{ fontSize: '0.78rem' }}><i className="fas fa-robot me-1"></i> OBSERVAÇÕES DA MEMOR.IA</strong>
                  <p className="mb-0 mt-1 fst-italic text-muted">{a.observacoes}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setAnaliseAberta(null)}>
                <i className="fas fa-times me-1"></i>Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="das-page">
        <div className="container-fluid mt-5 text-center">
          <div className="spinner-border text-warning" style={{ width: '3rem', height: '3rem' }}></div>
          <p className="mt-3 text-muted">Carregando suas análises...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="das-page" style={{ zoom: 1.1 }}>
      <div className="container-fluid mt-4">

        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="mb-4">
                      <span className="badge bg-light text-dark me-2" style={{ fontSize: '20px', fontWeight: '500' }}>
                        Painel do Operador — Memor.IA
                      </span>
                      {infoTipo ? (
                        <span className="badge" style={{ fontSize: '15px', fontWeight: '500', background: infoTipo.cor, color: '#fff' }}>
                          <i className={`fas ${infoTipo.icone} me-1`}></i>
                          {infoTipo.label}
                        </span>
                      ) : (
                        <span className="badge bg-warning text-dark" style={{ fontSize: '15px', fontWeight: '500' }}>
                          <i className="fas fa-brain me-1"></i>
                          Análise com Memor.IA
                        </span>
                      )}
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-chart-line header-icon me-2"></i>
                      <h2 className="titulo-pagina m-0">Meu Painel de Desempenho</h2>
                    </div>
                    <p className="mb-1 opacity-75">
                      <i className="fas fa-robot me-2"></i>
                      {infoTipo ? infoTipo.subtitulo : 'Análise inteligente da sua evolução como operador'}
                    </p>
                  </div>
                  <div className="mb-2">
                    <button
                      className="btn btn-sm"
                      onClick={() => navigate('/Contabilidade')}
                      style={{ backgroundColor: '#00205b', borderColor: '#00205b', color: '#fff' }}
                    >
                      <i className="fas fa-arrow-left me-1"></i>Voltar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {/* Aviso: operador ainda sem setor definido */}
        {!tipoAnalise && analises.length > 0 && (
          <div className="card border-0 shadow-sm mb-4" style={{ borderLeft: '4px solid #fd7e14' }}>
            <div className="card-body d-flex align-items-center gap-3 flex-wrap py-3">
              <i className="fas fa-sitemap fa-lg" style={{ color: '#fd7e14' }}></i>
              <div className="flex-grow-1" style={{ minWidth: 260 }}>
                <strong>Defina o seu setor</strong>
                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                  Informe se você é de Serviço, Comercial ou CS para o painel mostrar as
                  competências certas do seu papel.
                </div>
              </div>
              <button
                className="btn btn-sm"
                onClick={() => navigate('/configuracao-calendario')}
                style={{ background: '#fd7e14', border: 'none', color: '#fff', fontWeight: 600 }}
              >
                <i className="fas fa-cog me-1"></i>Definir setor
              </button>
            </div>
          </div>
        )}

        {analises.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
              <h5 className="text-muted">Nenhuma análise encontrada para seu usuário.</h5>
            </div>
          </div>
        ) : (
          <>
            {/* ── KPIs ────────────────────────────────────────────────── */}
            <div className="row g-3 mb-4">
              {[
                {
                  icon: 'fa-calendar-check',
                  cor: '#fd7e14',
                  valor: analises.length,
                  label: 'Reuniões Analisadas',
                  sub: 'total registrado',
                },
                {
                  icon: 'fa-chart-bar',
                  cor: '#198754',
                  valor: mediaNota,
                  label: 'Nota Média Geral',
                  sub: 'pontos de 0 a 10',
                },
                {
                  icon: 'fa-medal',
                  cor: '#0d6efd',
                  valor: analises.filter(a => (parseFloat(a.nota) || 0) >= 8).length,
                  label: 'Notas ≥ 8',
                  sub: 'reuniões excelentes',
                },
                {
                  icon: dadosEvol.length >= 2 && dadosEvol[dadosEvol.length - 1].media < dadosEvol[dadosEvol.length - 2].media
                    ? 'fa-arrow-trend-down' : 'fa-arrow-trend-up',
                  cor: dadosEvol.length >= 2
                    ? (dadosEvol[dadosEvol.length - 1].media >= dadosEvol[dadosEvol.length - 2].media ? '#198754' : '#dc3545')
                    : '#6c757d',
                  valor: dadosEvol.length >= 2
                    ? `${dadosEvol[dadosEvol.length - 1].media >= dadosEvol[dadosEvol.length - 2].media ? '↑' : '↓'} ${Math.abs(dadosEvol[dadosEvol.length - 1].media - dadosEvol[dadosEvol.length - 2].media).toFixed(1)}`
                    : '—',
                  label: 'Tendência Recente',
                  sub: 'último período',
                },
              ].map((k, i) => (
                <div key={i} className="col-6 col-md-3">
                  <div className="card border-0 shadow-sm h-100" style={{ borderTop: `3px solid ${k.cor}` }}>
                    <div className="card-body py-3">
                      <div className="fw-bold lh-1 mb-1" style={{ fontSize: '1.65rem', color: k.cor }}>{k.valor}</div>
                      <div className="fw-semibold text-dark" style={{ fontSize: '0.82rem' }}>{k.label}</div>
                      <div className="text-muted" style={{ fontSize: '0.72rem' }}>{k.sub}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Me ajude a entrar na reunião ─────────────────────────── */}
            {clientesDoOperador.length > 0 && (
              <div className="card border-0 shadow-sm mb-4" style={{ borderLeft: '4px solid #198754' }}>
                <div className="card-body py-3">
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div className="flex-grow-1" style={{ minWidth: 260 }}>
                      <strong>
                        <i className="fas fa-door-open me-2" style={{ color: '#198754' }}></i>
                        Me ajude a entrar na reunião
                      </strong>
                      <div className="text-muted" style={{ fontSize: '0.88rem' }}>
                        Escolha o cliente e a Memor.IA resume todas as suas reuniões com ele,
                        diz como abrir a conversa e os pontos que você pode trazer.
                      </div>
                    </div>
                    <select
                      className="form-select form-select-sm"
                      style={{ maxWidth: 280 }}
                      value={clientePrep}
                      onChange={e => setClientePrep(e.target.value)}
                    >
                      <option value="">Selecione o cliente...</option>
                      {clientesDoOperador.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <button
                      className="btn fw-semibold btn-preparar-reuniao"
                      onClick={pedirPreparacao}
                      disabled={!clientePrep || carregandoPrep}
                      style={{
                        background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)',
                        border: 'none', color: '#fff', borderRadius: 12,
                        padding: '0.45rem 1.4rem',
                        boxShadow: '0 2px 8px rgba(25,135,84,0.3)',
                      }}
                    >
                      {carregandoPrep
                        ? <><span className="spinner-border spinner-border-sm me-2"></span>Preparando...</>
                        : <>Preparar minha reunião</>}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Evolução no tempo ────────────────────────────────────── */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">
                  <i className="fas fa-chart-area me-2" style={{ color: '#fd7e14' }}></i>
                  Evolução da Nota no Tempo
                </h5>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-warning text-dark">
                    <i className="fas fa-star me-1"></i>Nota Média
                  </span>
                  <div className="btn-group btn-group-sm ms-2">
                    {[['diario', 'Diário'], ['semanal', 'Semanal'], ['mensal', 'Mensal']].map(([m, rotulo]) => (
                      <button
                        key={m}
                        className={`btn ${periodoEvol === m ? 'btn-warning' : 'btn-outline-secondary'}`}
                        onClick={() => setPeriodoEvol(m)}
                      >
                        {rotulo}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="card-body">
                {dadosEvol.length < 2 ? (
                  <p className="text-muted text-center py-3">Dados insuficientes para gerar o gráfico de evolução.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <AreaChart data={dadosEvol} margin={{ top: 10, right: 25, bottom: 5, left: 0 }}>
                      <defs>
                        <linearGradient id="gradientNota" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#fd7e14" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="#fd7e14" stopOpacity={0.03} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="periodo"
                        tickFormatter={v => formatarPeriodo(v, periodoEvol)}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis
                        domain={[0, 10]}
                        ticks={[0, 2, 4, 6, 8, 10]}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip content={<TooltipEvol modo={periodoEvol} />} />
                      <ReferenceLine
                        y={7}
                        stroke="#198754"
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        label={{ value: '← Abaixo | Meta 7 →', position: 'insideTopRight', fontSize: 11, fill: '#198754' }}
                      />
                      <Area
                        type="monotone"
                        dataKey="media"
                        stroke="#fd7e14"
                        strokeWidth={3}
                        fill="url(#gradientNota)"
                        dot={<CustomDotNota />}
                        activeDot={{ r: 8, fill: '#fd7e14', stroke: 'white', strokeWidth: 2 }}
                        name="Nota Média"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
                <div className="mt-2 text-center">
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    Média das notas por {periodoEvol === 'mensal' ? 'mês' : periodoEvol === 'semanal' ? 'semana' : 'dia'} — linha verde tracejada indica a meta de 7 pontos
                  </small>
                </div>
              </div>
            </div>

            {/* ── Radar de Competências ────────────────────────────────── */}
            <div className="row g-4 mb-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h6 className="mb-0">
                      <i className="fas fa-bullseye me-2 text-warning"></i>
                      Radar de Competências
                      {infoTipo && (
                        <span className="badge ms-2" style={{ background: infoTipo.cor, fontSize: '0.72rem' }}>
                          {infoTipo.label}
                        </span>
                      )}
                    </h6>
                    {radarIA && (
                      <small className="text-muted">Avaliado pela Memor.IA nas suas reuniões, com evidências</small>
                    )}
                  </div>
                  <div className="card-body">
                    <ResponsiveContainer width="100%" height={420}>
                      <RadarChart data={dadosRadar}>
                        <PolarGrid stroke="#e9ecef" />
                        <PolarAngleAxis dataKey="competencia" tick={{ fontSize: 11 }} />
                        <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fontSize: 10 }} />
                        <Radar
                          name="Score"
                          dataKey="score"
                          stroke="#fd7e14"
                          fill="#fd7e14"
                          fillOpacity={0.35}
                        />
                        <Tooltip formatter={(v) => [`${v}/10`, 'Score']} />
                      </RadarChart>
                    </ResponsiveContainer>
                    <div className="table-responsive mt-2">
                      <table className="table table-sm table-hover mb-0">
                        <tbody>
                          {dadosRadar.map((d, i) => (
                            <tr key={i}>
                              <td className="small">{d.competencia}</td>
                              <td>
                                <div className="progress" style={{ height: '8px', width: '100px' }}>
                                  <div
                                    className={`progress-bar ${d.score >= 7 ? 'bg-success' : d.score >= 5 ? 'bg-warning' : 'bg-danger'}`}
                                    style={{ width: `${d.score * 10}%` }}
                                  ></div>
                                </div>
                              </td>
                              <td className="small fw-bold">{d.score}/10</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pontos fortes / melhorias */}
              <div className="col-md-6">
                {/* Destaque e foco de desenvolvimento (dados da IA) */}
                {compDestaque && compFoco && (
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <div className="card border-0 shadow-sm h-100" style={{ borderTop: '3px solid #198754' }}>
                        <div className="card-body py-3 text-center">
                          <small className="text-muted d-block mb-1">
                            <i className="fas fa-trophy me-1" style={{ color: '#198754' }}></i>
                            Sua competência destaque
                          </small>
                          <div className="fw-bold" style={{ fontSize: '0.95rem' }}>{compDestaque.competencia}</div>
                          <span className="badge bg-success mt-1">{compDestaque.score}/10</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="card border-0 shadow-sm h-100" style={{ borderTop: '3px solid #dc3545' }}>
                        <div className="card-body py-3 text-center">
                          <small className="text-muted d-block mb-1">
                            <i className="fas fa-crosshairs me-1" style={{ color: '#dc3545' }}></i>
                            Foco de desenvolvimento
                          </small>
                          <div className="fw-bold" style={{ fontSize: '0.95rem' }}>{compFoco.competencia}</div>
                          <span className="badge bg-danger mt-1">{compFoco.score}/10</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-header bg-white">
                    <h6 className="mb-0">
                      <i className="fas fa-thumbs-up me-2 text-success"></i>
                      Pontos Fortes Recorrentes
                    </h6>
                    {radarIA && <small className="text-muted">Competências com nota ≥ 7 nas avaliações da Memor.IA</small>}
                  </div>
                  <div className="card-body">
                    {topFortes.length === 0 ? (
                      <p className="text-muted small mb-0">Dados insuficientes.</p>
                    ) : (
                      <ResponsiveContainer width="100%" height={Math.max(140, topFortes.length * 36)}>
                        <BarChart data={topFortes} layout="vertical" margin={{ left: 0, right: 35, top: 4, bottom: 4 }}>
                          <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={175} />
                          <Tooltip formatter={(v) => [v, 'Ocorrências']} />
                          <Bar dataKey="value" fill="#198754" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 11 }} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white">
                    <h6 className="mb-0">
                      <i className="fas fa-arrow-up me-2 text-danger"></i>
                      Principais Áreas de Melhoria
                    </h6>
                    {radarIA && <small className="text-muted">Competências não demonstradas ou com nota abaixo de 7</small>}
                  </div>
                  <div className="card-body">
                    {topMelhorias.length === 0 ? (
                      radarIA ? (
                        <p className="text-success small mb-0">
                          <i className="fas fa-check-circle me-1"></i>
                          Nenhuma competência abaixo de 7 nas suas avaliações — continue assim!
                        </p>
                      ) : (
                        <p className="text-muted small mb-0">Dados insuficientes.</p>
                      )
                    ) : (
                      <ResponsiveContainer width="100%" height={Math.max(140, topMelhorias.length * 36)}>
                        <BarChart data={topMelhorias} layout="vertical" margin={{ left: 0, right: 35, top: 4, bottom: 4 }}>
                          <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={175} />
                          <Tooltip formatter={(v) => [v, 'Ocorrências']} />
                          <Bar dataKey="value" fill="#dc3545" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 11 }} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Talento além do setor ───────────────────────────────── */}
            {talentosForaDoSetor.length > 0 && (
              <div className="card border-0 shadow-sm mb-4" style={{ borderLeft: '4px solid #ffc107' }}>
                <div className="card-header bg-white" style={{ borderBottom: '2px solid #ffc107' }}>
                  <h6 className="mb-0 fw-bold">
                    <i className="fas fa-star me-2" style={{ color: '#ffc107' }}></i>
                    Talento Além do Seu Setor
                  </h6>
                  <small className="text-muted">
                    A Memor.IA identificou competências de outras áreas em que você se destaca — vale conversar com seu gestor
                  </small>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {talentosForaDoSetor.map((t, i) => (
                      <div key={i} className="col-md-6 col-lg-4">
                        <div className="p-3 rounded-3 h-100" style={{ background: '#fffbee', border: '1px solid #ffe69c' }}>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <strong style={{ fontSize: '0.95rem' }}>{t.label}</strong>
                            <span className="badge bg-success">{t.media}/10</span>
                          </div>
                          <div className="text-muted mb-1" style={{ fontSize: '0.82rem' }}>
                            Competência de <strong>{t.areas.join(' e ') || 'outra área'}</strong> · {t.ocorrencias} avaliações
                          </div>
                          {t.evidencia && (
                            <div className="fst-italic text-muted" style={{ fontSize: '0.8rem' }}>
                              “{t.evidencia}”
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Clientes em Alerta ──────────────────────────────────── */}
            {clientesAlerta.length > 0 && (() => {
              const perda   = clientesAlerta.filter(c => c.menorNota < 6);
              const atencao = clientesAlerta.filter(c => c.menorNota === 6);
              return (
                <div className="row g-4 mb-4">
                  {atencao.length > 0 && (
                    <div className="col-md-6">
                      <div className="card border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #fd7e14 !important' }}>
                        <div className="card-header bg-white d-flex align-items-center gap-2" style={{ borderBottom: '2px solid #fd7e14' }}>
                          <i className="fas fa-exclamation-triangle" style={{ color: '#fd7e14' }}></i>
                          <h6 className="mb-0 fw-bold" style={{ color: '#fd7e14' }}>
                            {tipoAnalise === 'comercial' ? 'Negociações que Precisam de Atenção' : 'Clientes que Necessitam Atenção'}
                          </h6>
                          <span className="badge ms-auto" style={{ background: '#fd7e14' }}>{atencao.length}</span>
                        </div>
                        <div className="card-body p-0">
                          <table className="table table-sm table-hover mb-0">
                            <thead className="table-light">
                              <tr>
                                <th className="ps-3">Empresa</th>
                                <th className="text-center">Pior Nota</th>
                                <th className="text-center">Reuniões</th>
                              </tr>
                            </thead>
                            <tbody>
                              {atencao.map((c, i) => (
                                <tr key={i}>
                                  <td className="ps-3 small fw-semibold">{c.empresa}</td>
                                  <td className="text-center">
                                    <span className="badge bg-warning text-dark">{c.menorNota}/10</span>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-secondary">{c.reunioes}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                  {perda.length > 0 && (
                    <div className="col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-white d-flex align-items-center gap-2" style={{ borderBottom: '2px solid #dc3545' }}>
                          <i className="fas fa-arrow-trend-down text-danger"></i>
                          <h6 className="mb-0 fw-bold text-danger">
                            {tipoAnalise === 'cs' ? 'Risco de Churn — Atue para Reter' :
                             tipoAnalise === 'comercial' ? 'Negociações em Risco de Perda' : 'Tendência de Perda'}
                          </h6>
                          <span className="badge bg-danger ms-auto">{perda.length}</span>
                        </div>
                        <div className="card-body p-0">
                          <table className="table table-sm table-hover mb-0">
                            <thead className="table-light">
                              <tr>
                                <th className="ps-3">Empresa</th>
                                <th className="text-center">Pior Nota</th>
                                <th className="text-center">Reuniões</th>
                              </tr>
                            </thead>
                            <tbody>
                              {perda.map((c, i) => (
                                <tr key={i}>
                                  <td className="ps-3 small fw-semibold">{c.empresa}</td>
                                  <td className="text-center">
                                    <span className="badge bg-danger">{c.menorNota}/10</span>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-secondary">{c.reunioes}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── Nuvens de palavras ───────────────────────────────────── */}
            <div className="row g-4 mb-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h6 className="mb-0">
                      <i className="fas fa-smile me-2 text-success"></i>
                     Palavras Positivas Mais Usadas
                    </h6>
                  </div>
                  <div className="card-body">
                    <NuvemPalavras palavras={palavrasPos} tipo="positivo" />
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h6 className="mb-0">
                      <i className="fas fa-frown me-2 text-danger"></i>
                       Palavras Negativas Mais Usadas
                    </h6>
                  </div>
                  <div className="card-body">
                    <NuvemPalavras palavras={palavrasNeg} tipo="negativo" />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Treinamento personalizado (CTA central) ─────────────────── */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body text-center py-4">
                <button
                  className="btn fw-semibold btn-preparar-reuniao"
                  onClick={pedirTreinamento}
                  disabled={carregandoTreino || (palavrasNeg.length === 0 && !(radarIA && radarIA.length) && topMelhorias.length === 0)}
                  style={{
                    background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)',
                    border: 'none', color: '#fff', borderRadius: 12,
                    padding: '0.65rem 2.2rem', fontSize: '1.05rem',
                    boxShadow: '0 2px 10px rgba(25,135,84,0.35)',
                  }}
                >
                  {carregandoTreino
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Gerando seu treinamento...</>
                    : <>Receber Treinamento Personalizado</>
                  }
                </button>
                <small className="text-muted d-block mt-2">
                  A Memor.IA monta um treinamento sob medida com base nas suas competências, palavras e pontos de melhoria
                </small>
              </div>
            </div>

            {/* ── Correlação palavras x notas ──────────────────────────── */}
            {correlacao.length >= 3 && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white">
                  <h6 className="mb-0">
                    <i className="fas fa-calculator me-2" style={{ color: '#6610f2' }}></i>
                    Correlação: Palavras × Nota Média
                  </h6>
                  <small className="text-muted">Palavras que aparecem em reuniões com notas mais altas ou mais baixas (mín. 2 ocorrências)</small>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Palavra</th>
                          <th>Tipo</th>
                          <th>Ocorrências</th>
                          <th>Nota Média das Reuniões</th>
                        </tr>
                      </thead>
                      <tbody>
                        {correlacao.map((p, i) => (
                          <tr key={i}>
                            <td><strong>{p.palavra}</strong></td>
                            <td>
                              <span className={`badge ${p.tipo === 'positiva' ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '0.7rem' }}>
                                {p.tipo}
                              </span>
                            </td>
                            <td>{p.count}×</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <div className="progress" style={{ width: '80px', height: '8px' }}>
                                  <div
                                    className={`progress-bar ${p.mediaNota >= 7 ? 'bg-success' : p.mediaNota >= 5 ? 'bg-warning' : 'bg-danger'}`}
                                    style={{ width: `${p.mediaNota * 10}%` }}
                                  ></div>
                                </div>
                                <span className={`badge ${p.mediaNota >= 7 ? 'bg-success' : p.mediaNota >= 5 ? 'bg-warning text-dark' : 'bg-danger'}`}>
                                  {p.mediaNota}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── Média por Cliente ───────────────────────────────────── */}
            {clientesMedias.length > 0 && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-0">
                      <i className="fas fa-building me-2" style={{ color: '#0d6efd' }}></i>
                      Média de Atendimento por Cliente
                    </h6>
                    <small className="text-muted">Clientes com menor média precisam de mais atenção — ordenados do pior para o melhor</small>
                  </div>
                  <div className="d-flex gap-2">
                    <span className="badge" style={{ background: '#dc3545' }}>Crítico &lt; 6</span>
                    <span className="badge" style={{ background: '#fd7e14' }}>Atenção 6–7</span>
                    <span className="badge" style={{ background: '#198754' }}>Bom ≥ 7</span>
                  </div>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={Math.max(200, clientesMedias.length * 36)}>
                    <BarChart
                      data={clientesMedias}
                      layout="vertical"
                      margin={{ left: 16, right: 60, top: 4, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis
                        type="number"
                        domain={[0, 10]}
                        ticks={[0, 2, 4, 6, 7, 8, 10]}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis
                        type="category"
                        dataKey="empresa"
                        tick={{ fontSize: 11 }}
                        width={180}
                      />
                      <Tooltip
                        formatter={(v, _, props) => [
                          `${v}/10 — ${props.payload.reunioes} reunião(ões)`,
                          'Média'
                        ]}
                      />
                      <ReferenceLine
                        x={7}
                        stroke="#198754"
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        label={{ value: 'Meta 7', position: 'top', fontSize: 10, fill: '#198754' }}
                      />
                      <Bar dataKey="media" radius={[0, 6, 6, 0]} label={{ position: 'right', fontSize: 11, formatter: v => `${v}/10` }}>
                        {clientesMedias.map((entry, index) => (
                          <Cell
                            key={index}
                            fill={entry.media < 6 ? '#dc3545' : entry.media < 7 ? '#fd7e14' : '#198754'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* ── Melhores e piores reuniões ───────────────────────────── */}
            {analises.length > 0 && (
              <div className="row g-4 mb-4">
                {[
                  { titulo: 'Suas Melhores Reuniões', sub: 'Modelos a repetir — o que funcionou aqui', cor: '#198754', icon: 'fa-trophy', dados: melhoresReunioes },
                  { titulo: 'Reuniões para Rever', sub: 'As de nota mais baixa — onde há mais a aprender', cor: '#dc3545', icon: 'fa-arrow-trend-down', dados: pioresReunioes },
                ].map((sec, si) => (
                  <div key={si} className="col-md-6">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-header bg-white" style={{ borderBottom: `2px solid ${sec.cor}` }}>
                        <h6 className="mb-0"><i className={`fas ${sec.icon} me-2`} style={{ color: sec.cor }}></i>{sec.titulo}</h6>
                        <small className="text-muted">{sec.sub}</small>
                      </div>
                      <div className="card-body p-0">
                        {sec.dados.length === 0 ? (
                          <p className="text-muted small p-3 mb-0">Sem reuniões suficientes.</p>
                        ) : (
                          <ul className="list-group list-group-flush">
                            {sec.dados.map((a, i) => (
                              <li key={i}
                                className="list-group-item d-flex justify-content-between align-items-center"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setAnaliseAberta(a)}>
                                <div className="text-truncate me-2" style={{ maxWidth: '75%' }}>
                                  <strong style={{ fontSize: '0.88rem' }}>{a.cliente_empresa || a.assunto || a.codigo_reuniao}</strong>
                                  <div className="text-muted text-truncate" style={{ fontSize: '0.78rem' }}>
                                    {a.assunto && a.cliente_empresa ? a.assunto + ' · ' : ''}{formatarData(a.data_reuniao)}
                                  </div>
                                </div>
                                <span className={`badge ${getBadgeNota(parseFloat(a.nota))}`}>{a.nota}/10</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Histórico de reuniões ────────────────────────────────── */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white">
                <h6 className="mb-0">
                  <i className="fas fa-list me-2 text-secondary"></i>
                  Histórico de Reuniões Analisadas ({analises.length})
                </h6>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Código</th>
                        <th>Reunião</th>
                        <th>Empresa</th>
                        <th>Tipo</th>
                        <th>Data</th>
                        <th>Analisada por</th>
                        <th>Nota</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {analises.map(a => (
                        <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => setAnaliseAberta(a)}>
                          <td><span className="badge" style={{ background: '#fd7e14' }}>{a.codigo_reuniao}</span></td>
                          <td className="text-truncate" style={{ maxWidth: 220 }} title={a.assunto || ''}>{a.assunto || '-'}</td>
                          <td>{a.cliente_empresa || '-'}</td>
                          <td><span className="badge bg-secondary" style={{ fontSize: '0.7rem' }}>{labelTipoReuniao(a.tipo_reuniao)}</span></td>
                          <td>{formatarData(a.data_reuniao)}</td>
                          <td>{a.analisado_por || '—'}</td>
                          <td>
                            <span className={`badge ${getBadgeNota(a.nota)}`}>{a.nota}/10</span>
                          </td>
                          <td><i className="fas fa-chevron-right text-muted"></i></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {renderModal()}

        {/* ── Modal Treinamento IA ─────────────────────────────────────── */}
        {showModalTreino && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
            onClick={() => setShowModalTreino(false)}
          >
            <div
              className="modal-dialog"
              style={{ maxWidth: '98vw', width: '1300px', margin: '0.4rem auto' }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-brain me-2"></i>
                      Treinamento Personalizado — Memor.IA
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {palavrasNeg.length} palavras negativas · {topMelhorias.length} áreas de melhoria analisadas
                    </small>
                  </div>
                  <button className="btn-close" onClick={() => setShowModalTreino(false)}></button>
                </div>
                <div className="modal-body" style={{ maxHeight: '88vh', overflowY: 'auto', padding: '2rem 2.5rem', fontSize: '0.97rem' }}>
                  {carregandoTreino ? (
                    <div className="text-center py-5">
                      <div className="spinner-border" style={{ color: '#6610f2', width: '3rem', height: '3rem' }}></div>
                      <p className="mt-3 text-muted">
                        <i className="fas fa-robot me-1"></i>
                        A Memor.IA está analisando suas palavras e áreas de melhoria...
                      </p>
                    </div>
                  ) : (
                    <div>
                      {renderBarraAudio(treinamento)}
                      {renderTreinamento(treinamento)}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowModalTreino(false)}>
                    <i className="fas fa-times me-1"></i>Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal Preparação para a Reunião ─────────────────────────── */}
        {showModalPrep && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
            onClick={() => setShowModalPrep(false)}
          >
            <div
              className="modal-dialog modal-dialog-scrollable"
              style={{ maxWidth: '95vw', width: '1100px', margin: '1rem auto' }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-1 fw-bold" style={{ color: '#212529' }}>
                      Preparação para a Reunião — Memor.IA
                    </h5>
                    <span className="badge" style={{ backgroundColor: '#198754', fontSize: '0.85rem' }}>
                      <i className="fas fa-building me-1"></i>{clientePrep}
                    </span>
                  </div>
                  <button className="btn-close" onClick={() => setShowModalPrep(false)}></button>
                </div>
                <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto', padding: '1.8rem 2.4rem', fontSize: '0.97rem' }}>
                  {carregandoPrep ? (
                    <div className="text-center py-5">
                      <div className="spinner-border mb-3" style={{ color: '#198754', width: '2.5rem', height: '2.5rem' }}></div>
                      <p className="text-muted fw-semibold">
                        A Memor.IA está revisando todas as suas reuniões com {clientePrep}...
                      </p>
                      <small className="text-muted">Isso pode levar alguns segundos</small>
                    </div>
                  ) : (
                    <div>
                      {renderBarraAudio(preparacao)}
                      {renderTreinamento(preparacao)}
                    </div>
                  )}
                </div>
                {!carregandoPrep && (
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowModalPrep(false)}>
                      <i className="fas fa-times me-1"></i>Fechar
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <style>{`
          .modal.show { display: block; }
          @keyframes pulsePrep {
            0% { box-shadow: 0 2px 8px rgba(25,135,84,0.3), 0 0 0 0 rgba(32,201,151,0.55); }
            70% { box-shadow: 0 2px 8px rgba(25,135,84,0.3), 0 0 0 14px rgba(32,201,151,0); }
            100% { box-shadow: 0 2px 8px rgba(25,135,84,0.3), 0 0 0 0 rgba(32,201,151,0); }
          }
          .btn-preparar-reuniao:hover:not(:disabled) {
            animation: pulsePrep 1.2s ease-out infinite;
            transform: scale(1.03);
          }
          .btn-preparar-reuniao { transition: transform 0.2s ease; }
        `}</style>
      </div>
    </div>
  );
};

export default Analise_op_memora;
